import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Select,
  DatePicker,
  Statistic,
  Row,
  Col,
  Drawer,
  Descriptions,
  Radio,
  Tabs,
} from 'antd';
import {
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { shadowLinkApi } from '../../services/api';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// 预定义颜色池（确保每条线都有独特的颜色）
const COLOR_PALETTE = [
  '#1890ff', '#52c41a', '#722ed1', '#faad14', '#eb2f96',
  '#13c2c2', '#f5222d', '#2f54eb', '#fa8c16', '#a0d911',
  '#73d13d', '#40a9ff', '#b37feb', '#ffc53d', '#ff7a45',
  '#36cfc9', '#ff4d4f', '#597ef7', '#d3f261', '#9254de',
];

// 场景颜色映射缓存
const scenarioColorCache = {};
let colorIndex = 0;

// 获取场景颜色的函数（确保每个场景有唯一颜色）
const getScenarioColor = (scenarioName) => {
  if (!scenarioColorCache[scenarioName]) {
    scenarioColorCache[scenarioName] = COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
    colorIndex++;
  }
  return scenarioColorCache[scenarioName];
};

function ShadowEvaluation() {
  const { currentScenario } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [timePeriod, setTimePeriod] = useState('DAY');
  const [selectedDomain, setSelectedDomain] = useState(undefined);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  // 筛选选项数据
  const [domainOptions, setDomainOptions] = useState([]);
  const [scenarioOptions, setScenarioOptions] = useState([]);

  // 抽屉状态
  const [reportDetailVisible, setReportDetailVisible] = useState(false);
  const [caseDetailVisible, setCaseDetailVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseFilter, setCaseFilter] = useState('ALL');

  // 统计数据
  const [stats, setStats] = useState({
    totalReports: 60,
    totalCases: 2243,
    avgConsistentRate: 80.0,
    totalInconsistentCases: 441,
  });

  // 获取业务域选项
  const fetchDomainOptions = async () => {
    try {
      const res = await shadowLinkApi.getBusinessDomains();
      setDomainOptions(res?.list || []);
    } catch (error) {
      console.error('获取业务域列表失败:', error);
      // 接口异常时使用mock数据（开发调试）
      setDomainOptions([
        { value: '采购', label: '采购' },
        { value: '合同', label: '合同' },
        { value: '财务', label: '财务' },
      ]);
    }
  };

  // 获取业务场景选项
  const fetchScenarioOptions = async (domain) => {
    try {
      const res = await shadowLinkApi.getBusinessScenarios(domain);
      setScenarioOptions(res?.list || []);
    } catch (error) {
      console.error('获取业务场景列表失败:', error);
      // 接口异常时使用mock数据（开发调试）
      if (domain === '采购') {
        setScenarioOptions([
          { value: '采购RT审核', label: '采购RT审核' },
          { value: '采购合规审查', label: '采购合规审查' },
        ]);
      } else if (domain === '合同') {
        setScenarioOptions([
          { value: '合同机审', label: '合同机审' },
          { value: '合同风险识别', label: '合同风险识别' },
        ]);
      } else {
        setScenarioOptions([
          { value: '采购RT审核', label: '采购RT审核' },
          { value: '采购合规审查', label: '采购合规审查' },
          { value: '合同机审', label: '合同机审' },
          { value: '合同风险识别', label: '合同风险识别' },
          { value: '证照合规审查', label: '证照合规审查' },
        ]);
      }
    }
  };

  // 处理业务域变更
  const handleDomainChange = (value) => {
    setSelectedDomain(value);
    setSelectedScenarios([]); // 清空已选业务场景
    if (value) {
      fetchScenarioOptions(value);
    } else {
      fetchScenarioOptions(); // 获取全部业务场景
    }
    // 业务域变更时刷新页面数据
    fetchData();
  };

  // 处理业务场景变更
  const handleScenarioChange = (value) => {
    setSelectedScenarios(value);
    // 业务场景变更时刷新页面数据
    fetchData();
  };

  useEffect(() => {
    fetchData();
    fetchDomainOptions();
    fetchScenarioOptions();
  }, [timePeriod]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 获取统计数据
      const statsRes = await shadowLinkApi.getEvaluationStats({
        businessDomain: selectedDomain,
        businessScenarios: selectedScenarios,
        timePeriod: timePeriod,
      });
      if (statsRes) {
        setStats({
          totalReports: statsRes.totalReports || 0,
          totalCases: statsRes.totalCases || 0,
          avgConsistentRate: statsRes.avgConsistentRate || 0,
          totalInconsistentCases: statsRes.totalInconsistentCases || 0,
        });
      }

      // 获取趋势数据
      const trendRes = await shadowLinkApi.getEvaluationTrend({
        businessDomain: selectedDomain,
        businessScenarios: selectedScenarios,
        timePeriod: timePeriod,
      });
      setTrendData(trendRes || []);

      // 获取报告列表数据
      const reportRes = await shadowLinkApi.getEvaluationList({
        businessDomain: selectedDomain,
        businessScenarios: selectedScenarios,
        timePeriod: timePeriod,
      });
      setReportData(reportRes?.list || []);
    } catch (error) {
      console.error('获取评测报告数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimePeriodChange = (e) => {
    setTimePeriod(e.target.value);
  };

  const handleViewReportDetail = (record) => {
    setSelectedReport(record);
    setReportDetailVisible(true);
    setCaseFilter('ALL');
    setCaseDetailPageNo(1); // 重置分页到第一页
    setCaseDetailPageSize(10);
    // 重置用例明细数据，等待接口返回新数据
    setCaseDetailData([]);
    setCaseDetailTotal(0);
  };

  const handleViewCaseDetail = (caseRecord) => {
    setSelectedCase(caseRecord);
    setCaseDetailVisible(true);
  };

  const handleCloseCaseDetail = () => {
    setCaseDetailVisible(false);
    setSelectedCase(null);
  };

  const handleCloseReportDetail = () => {
    setReportDetailVisible(false);
    setSelectedReport(null);
    // 关闭报告详情时同时关闭用例详情
    setCaseDetailVisible(false);
    setSelectedCase(null);
    // 重置用例明细数据
    setCaseDetailData([]);
    setCaseDetailTotal(0);
    setCaseDetailPageNo(1);
  };

  const getRowClassName = (record) => {
    if (record.consistentRate < 90) {
      return 'bg-red-50';
    }
    if (record.consistentRate >= 95) {
      return 'bg-green-50';
    }
    return '';
  };

  const getAssertionResultTag = (result) => {
    switch (result) {
      case 'CONSISTENT':
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            一致
          </Tag>
        );
      case 'INCONSISTENT':
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            不一致
          </Tag>
        );
      case 'FAILED':
        return (
          <Tag icon={<ExclamationCircleOutlined />} color="default">
            失败
          </Tag>
        );
      default:
        return <Tag>未知</Tag>;
    }
  };

  const getDecisionTag = (decision) => {
    if (decision === '通过') {
      return <Tag color="success">通过</Tag>;
    }
    if (decision === '拒绝') {
      return <Tag color="error">拒绝</Tag>;
    }
    return <Tag>-</Tag>;
  };

  // 用例明细数据状态
  const [caseDetailData, setCaseDetailData] = useState([]);
  const [caseDetailTotal, setCaseDetailTotal] = useState(0);
  const [caseDetailPageNo, setCaseDetailPageNo] = useState(1);
  const [caseDetailPageSize, setCaseDetailPageSize] = useState(10);
  const [caseDetailLoading, setCaseDetailLoading] = useState(false);

  // 将 Date 格式化为 YYYY-MM-DD HH:mm:ss
  const formatDateTime = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // 将周期字符串转换为时间范围
  const parsePeriodToDateRange = (period) => {
    if (!period) return null;
    
    // 匹配 "2024年12月" 格式
    const monthMatch = period.match(/(\d{4})年(\d{1,2})月$/);
    if (monthMatch) {
      const year = parseInt(monthMatch[1], 10);
      const month = parseInt(monthMatch[2], 10);
      const startDate = new Date(year, month - 1, 1, 0, 0, 0);
      const endDate = new Date(year, month, 0, 23, 59, 59);
      return {
        startTime: formatDateTime(startDate),
        endTime: formatDateTime(endDate),
      };
    }
    
    // 匹配 "2024年12月第1周" 格式
    const weekMatch = period.match(/(\d{4})年(\d{1,2})月第(\d+)周/);
    if (weekMatch) {
      const year = parseInt(weekMatch[1], 10);
      const month = parseInt(weekMatch[2], 10);
      const week = parseInt(weekMatch[3], 10);
      // 计算该月第几周的开始日期（简化处理：每周从周一开始）
      const firstDayOfMonth = new Date(year, month - 1, 1);
      const firstMonday = new Date(firstDayOfMonth);
      firstMonday.setDate(firstDayOfMonth.getDate() + ((8 - firstDayOfMonth.getDay()) % 7));
      const startDate = new Date(firstMonday);
      startDate.setDate(firstMonday.getDate() + (week - 1) * 7);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59);
      return {
        startTime: formatDateTime(startDate),
        endTime: formatDateTime(endDate),
      };
    }
    
    // 匹配 "2024-12-01" 日期格式
    const dateMatch = period.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const year = parseInt(dateMatch[1], 10);
      const month = parseInt(dateMatch[2], 10) - 1;
      const day = parseInt(dateMatch[3], 10);
      const startDate = new Date(year, month, day, 0, 0, 0);
      const endDate = new Date(year, month, day, 23, 59, 59);
      return {
        startTime: formatDateTime(startDate),
        endTime: formatDateTime(endDate),
      };
    }
    
    return null;
  };

  // 获取当前报告的用例明细 - 使用数据上报列表接口，带分页
  const fetchCaseDetailData = async () => {
    if (!selectedReport) return;
    
    // 从行数据中获取业务域ID和业务场景ID
    const businessDomainId = selectedReport.businessDomainId || selectedReport.domainId;
    const businessScenarioId = selectedReport.businessScenarioId || selectedReport.scenarioId;
    
    if (!businessDomainId || !businessScenarioId) {
      console.warn('缺少业务域ID或业务场景ID，无法获取用例明细', selectedReport);
      setCaseDetailData([]);
      setCaseDetailTotal(0);
      return;
    }
    
    setCaseDetailLoading(true);
    try {
      // 将周期字符串转换为时间范围
      const dateRange = parsePeriodToDateRange(selectedReport.period);
      
      // 使用数据上报列表接口，带上业务域ID、业务场景ID、时间范围等参数
      const params = {
        pageNo: caseDetailPageNo,
        pageSize: caseDetailPageSize,
        businessDomainId: businessDomainId,
        businessScenarioId: businessScenarioId,
        ...(dateRange && {
          startTime: dateRange.startTime,
          endTime: dateRange.endTime,
        }),
        // 根据筛选条件添加断言结果过滤
        ...(caseFilter !== 'ALL' && { assertionResult: caseFilter }),
      };
      console.log('获取用例明细请求参数:', params);
      const res = await shadowLinkApi.getReportList(params);
      setCaseDetailData(res?.list || []);
      setCaseDetailTotal(res?.total || 0);
    } catch (error) {
      console.error('获取用例明细数据失败:', error);
      setCaseDetailData([]);
      setCaseDetailTotal(0);
    } finally {
      setCaseDetailLoading(false);
    }
  };

  // 当选择报告、筛选条件或分页变化时，重新获取用例明细
  useEffect(() => {
    if (selectedReport && reportDetailVisible) {
      fetchCaseDetailData();
    }
  }, [selectedReport, caseFilter, reportDetailVisible, caseDetailPageNo, caseDetailPageSize]);

  // 处理分页变化
  const handleCaseDetailPageChange = (page, pageSize) => {
    setCaseDetailPageNo(page);
    if (pageSize) {
      setCaseDetailPageSize(pageSize);
    }
  };

  const reportColumns = [
    {
      title: '统计周期',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: '业务域',
      dataIndex: 'businessDomain',
      key: 'businessDomain',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '业务场景',
      dataIndex: 'businessScenario',
      key: 'businessScenario',
      render: (text) => <Tag color="purple">{text}</Tag>,
    },
    {
      title: '总用例数',
      dataIndex: 'totalCases',
      key: 'totalCases',
    },
    {
      title: '一致用例',
      dataIndex: 'consistentCases',
      key: 'consistentCases',
      render: (text) => <span className="text-green-600">{text}</span>,
    },
    {
      title: '不一致用例',
      dataIndex: 'inconsistentCases',
      key: 'inconsistentCases',
      render: (text) => <span className="text-red-600">{text}</span>,
    },
    {
      title: '一致率',
      dataIndex: 'consistentRate',
      key: 'consistentRate',
      render: (value) => {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (numValue === undefined || numValue === null || isNaN(numValue)) return '-';
        let color = '#faad14';
        let bgColor = '#fff7e6';
        if (numValue >= 95) {
          color = '#52c41a';
          bgColor = '#f6ffed';
        }
        if (numValue < 90) {
          color = '#f5222d';
          bgColor = '#fff1f0';
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 6, backgroundColor: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
              <div 
                style={{ 
                  width: `${numValue}%`, 
                  height: '100%', 
                  backgroundColor: color,
                  borderRadius: 3,
                  transition: 'width 0.3s ease'
                }} 
              />
            </div>
            <span style={{ color, fontWeight: 'bold', minWidth: 50, textAlign: 'right' }}>
              {numValue.toFixed(0)}%
            </span>
          </div>
        );
      },
    },
    {
      title: '报告生成时间',
      dataIndex: 'reportTime',
      key: 'reportTime',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewReportDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">评测报告</h1>
          <p className="page-description">查看和管理影子链路评测报告及趋势分析</p>
        </div>
      </div>

      {/* 筛选栏 */}
      <Card className="mb-4">
        <Space size="middle" align="center">
          <span>业务域：</span>
          <Select
            placeholder="全部业务域"
            value={selectedDomain}
            onChange={handleDomainChange}
            style={{ width: 150 }}
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={domainOptions}
          />
          <span>业务场景：</span>
          <Select
            mode="multiple"
            placeholder={selectedDomain ? '请选择业务场景' : '请先选择业务域'}
            value={selectedScenarios}
            onChange={handleScenarioChange}
            style={{ width: 280 }}
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            disabled={!selectedDomain}
            options={scenarioOptions}
          />
          <span>时间周期：</span>
          <Radio.Group value={timePeriod} onChange={handleTimePeriodChange}>
            <Radio.Button value="DAY">日</Radio.Button>
            <Radio.Button value="WEEK">周</Radio.Button>
            <Radio.Button value="MONTH">月</Radio.Button>
          </Radio.Group>
        </Space>
      </Card>

      {/* 统计卡片 */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="历史报告数"
              value={stats.totalReports}
              suffix="份"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="累计用例数"
              value={stats.totalCases}
              suffix="条"
              valueStyle={{ color: '#262626' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均一致率"
              value={stats.avgConsistentRate}
              suffix="%"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="累计不一致用例"
              value={stats.totalInconsistentCases}
              suffix="条"
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 趋势图 */}
      <Card className="mb-6" title="一致率趋势">
        <div style={{ width: '100%', height: 350 }}>
          <ResponsiveContainer>
            <LineChart data={trendData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              <ReferenceLine y={95} stroke="#52c41a" strokeDasharray="5 5" label="95% 优秀线" />
              <ReferenceLine y={90} stroke="#f5222d" strokeDasharray="5 5" label="90% 警戒线" />
              {trendData.length > 0 &&
                Object.keys(trendData[0])
                  .filter((key) => key !== 'period')
                  .map((scenario) => (
                    <Line
                      key={scenario}
                      type="monotone"
                      dataKey={scenario}
                      stroke={getScenarioColor(scenario)}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* 报告列表 */}
      <Card
        title={
          <Space>
            <span>历史报告列表</span>
            <span className="text-gray-400 text-sm">
              共 {reportData.length} 份 · 
              {timePeriod === 'DAY' ? '按日聚合' : timePeriod === 'WEEK' ? '按周聚合' : '按月聚合'}
            </span>
          </Space>
        }
        extra={
          <Space>
            <FilterOutlined />
            <span>精确筛选：</span>
            <DatePicker picker="month" placeholder="选择具体月份" />
          </Space>
        }
      >
        <Table
          columns={reportColumns}
          dataSource={reportData}
          rowKey="id"
          loading={loading}
          rowClassName={getRowClassName}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      {/* 报告详情抽屉 */}
      <Drawer
        title={
          <Space>
            <span>报告详情</span>
          </Space>
        }
        width={800}
        open={reportDetailVisible}
        onClose={handleCloseReportDetail}
        push={false}
      >
        {selectedReport && (
          <>
            {/* 面包屑 */}
            <div className="mb-4">
              <Space>
                <Tag color="blue">{selectedReport.businessDomain}</Tag>
                <span>/</span>
                <Tag color="purple">{selectedReport.businessScenario}</Tag>
                <span>/</span>
                <Tag>{selectedReport.period}</Tag>
              </Space>
            </div>

            {/* 核心指标 */}
            <Row gutter={16} className="mb-6">
              <Col span={8}>
                <Card 
                  className="text-center" 
                  style={{ backgroundColor: '#fff7e6', border: 'none' }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <div className="text-3xl font-bold" style={{ color: '#faad14' }}>
                    {(() => {
                      const value = typeof selectedReport.consistentRate === 'string' 
                        ? parseFloat(selectedReport.consistentRate) 
                        : selectedReport.consistentRate;
                      return !isNaN(value) ? value.toFixed(0) : '-';
                    })()}%
                  </div>
                  <div className="mt-1" style={{ color: '#d48806' }}>一致率</div>
                </Card>
              </Col>
              <Col span={8}>
                <Card 
                  className="text-center" 
                  style={{ backgroundColor: '#f6ffed', border: 'none' }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <div className="text-3xl font-bold" style={{ color: '#52c41a' }}>
                    {selectedReport.consistentCases}
                  </div>
                  <div className="mt-1" style={{ color: '#389e0d' }}>一致用例</div>
                </Card>
              </Col>
              <Col span={8}>
                <Card 
                  className="text-center" 
                  style={{ backgroundColor: '#fff1f0', border: 'none' }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <div className="text-3xl font-bold" style={{ color: '#f5222d' }}>
                    {selectedReport.inconsistentCases}
                  </div>
                  <div className="mt-1" style={{ color: '#cf1322' }}>不一致用例</div>
                </Card>
              </Col>
            </Row>

            {/* 基本信息 */}
            <Descriptions bordered column={2} className="mb-6">
              <Descriptions.Item label="统计周期" span={2}>{selectedReport.period}</Descriptions.Item>
              <Descriptions.Item label="业务域">
                <Tag color="blue">{selectedReport.businessDomain}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="业务场景">
                <Tag color="purple">{selectedReport.businessScenario}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="总用例数" span={2}>{selectedReport.totalCases} 条</Descriptions.Item>
              <Descriptions.Item label="报告生成时间" span={2}>
                {selectedReport.reportTime}
              </Descriptions.Item>
            </Descriptions>

            {/* 用例明细 */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">
                  <span className="text-blue-500 mr-2">
                    <EyeOutlined />
                  </span>
                  用例明细 ({caseDetailTotal} 条)
                </h3>
                <Radio.Group value={caseFilter} onChange={(e) => {
                  setCaseFilter(e.target.value);
                  setCaseDetailPageNo(1); // 切换筛选时重置到第一页
                }}>
                  <Radio.Button value="ALL">全部</Radio.Button>
                  <Radio.Button value="CONSISTENT">一致</Radio.Button>
                  <Radio.Button value="INCONSISTENT">不一致</Radio.Button>
                </Radio.Group>
              </div>
              <div className="space-y-3">
                {caseDetailData.map((item) => (
                  <div
                    key={item.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      item.assertionResult === 'CONSISTENT'
                        ? 'bg-green-50 border-green-200 hover:border-green-400'
                        : 'bg-red-50 border-red-200 hover:border-red-400'
                    }`}
                    onClick={() => handleViewCaseDetail(item)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-blue-600 font-medium hover:underline">{item.id}</span>
                      <div className="flex items-center gap-2">
                        {getAssertionResultTag(item.assertionResult)}
                        <EyeOutlined className="text-gray-400 text-sm" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {caseDetailTotal > 0 && (
                <div className="mt-4 flex justify-end">
                  <div className="text-sm text-gray-500">
                    共 {caseDetailTotal} 条
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </Drawer>

      {/* 嵌套用例详情抽屉 */}
      <Drawer
        title={
          <Space>
            <span>用例明细</span>
            {selectedCase && <span className="text-gray-400">- {selectedCase.id}</span>}
          </Space>
        }
        width={600}
        open={caseDetailVisible}
        onClose={handleCloseCaseDetail}
        push={false}
        zIndex={1001}
      >
        {selectedCase && (
          <>
            {/* 面包屑 */}
            <div className="mb-4">
              <Space>
                <Tag color="blue">{selectedReport?.businessDomain}</Tag>
                <span>/</span>
                <Tag color="purple">{selectedReport?.businessScenario}</Tag>
                <span>/</span>
                <Tag>{selectedReport?.period}</Tag>
              </Space>
            </div>

            {/* 决策一致性状态 */}
            <div className="mb-6">
              {selectedCase.assertionResult === 'CONSISTENT' ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                  <CheckCircleOutlined className="text-green-500 text-xl mr-3" />
                  <div className="text-green-700 font-medium">AI 与人工决策一致</div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
                  <CloseCircleOutlined className="text-red-500 text-xl mr-3" />
                  <div className="text-red-700 font-medium">AI 与人工决策不一致</div>
                </div>
              )}
            </div>

            {/* 用例基本信息 */}
            <Descriptions bordered column={1} className="mb-6">
              <Descriptions.Item label="用例ID">
                <span className="text-blue-600 font-medium">{selectedCase.id}</span>
              </Descriptions.Item>
              <Descriptions.Item label="上报时间">{selectedCase.reportTime}</Descriptions.Item>
              <Descriptions.Item label="数据来源">
                <Tag color="blue">线上流量</Tag>
              </Descriptions.Item>
            </Descriptions>

            {/* AI审核意见 */}
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-4 flex items-center">
                <span className="w-1 h-5 bg-blue-500 rounded mr-2"></span>
                AI 审核意见
              </h3>
              {selectedCase.assertionResult === 'FAILED' ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-500">
                  断言执行失败
                </div>
              ) : selectedCase.aiReasons === undefined || selectedCase.aiReasons === null ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-500">
                  无AI审核意见
                </div>
              ) : Array.isArray(selectedCase.aiReasons) ? (
                <div className="space-y-3">
                  {selectedCase.aiReasons.map((reason, index) => (
                    <div
                      key={index}
                      className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start"
                    >
                      <CheckCircleOutlined className="text-green-500 mt-1 mr-3" />
                      <div>
                        <span className="text-green-700 font-medium">[{reason.code}]</span>
                        <span className="text-gray-700 ml-2">{reason.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : typeof selectedCase.aiReasons === 'object' ? (
                <div className="space-y-3">
                  {Object.entries(selectedCase.aiReasons).map(([code, text], index) => (
                    <div
                      key={index}
                      className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start"
                    >
                      <CheckCircleOutlined className="text-green-500 mt-1 mr-3" />
                      <div>
                        <span className="text-green-700 font-medium">[{code}]</span>
                        <span className="text-gray-700 ml-2">{text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
                  <CheckCircleOutlined className="text-green-500 mt-1 mr-3" />
                  <p className="text-gray-700">{String(selectedCase.aiReasons)}</p>
                </div>
              )}
            </div>

            {/* 人工审核意见 */}
            <div>
              <h3 className="text-lg font-medium mb-4 flex items-center">
                <span className="w-1 h-5 bg-orange-500 rounded mr-2"></span>
                人工审核意见
              </h3>
              {selectedCase.humanOpinion === undefined || selectedCase.humanOpinion === null ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-500">
                  无人工审核意见
                </div>
              ) : Array.isArray(selectedCase.humanOpinion) ? (
                <div className="space-y-3">
                  {selectedCase.humanOpinion.map((opinion, index) => (
                    <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-gray-700">{opinion}</p>
                    </div>
                  ))}
                </div>
              ) : typeof selectedCase.humanOpinion === 'object' ? (
                <div className="space-y-3">
                  {Object.entries(selectedCase.humanOpinion).map(([key, value], index) => (
                    <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-gray-700"><span className="font-medium">{key}:</span> {value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-gray-700">{String(selectedCase.humanOpinion)}</p>
                </div>
              )}
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}

export default ShadowEvaluation;
